import React from "react";
import dayjs from "dayjs";
import { Box, Icon, IconButton, Stack, Text } from "@chakra-ui/react";
import { MdClose } from "react-icons/md";
import {
    TIER_LABELS,
    TIER_COLORS,
    STATUS_LABELS,
    STATUS_COLORS,
    PROVIDER_LABELS,
    DEFAULT_CHIP_COLOR,
} from "./subscriptionsConstants";

/**
 * Builds chip-style Box props from a tier or status color scheme.
 * @param {Object} scheme - Color scheme with bg, color, border
 * @returns {Object} Chakra style props
 */
function chipBoxProps(scheme) {
    return {
        background: scheme.bg,
        color: scheme.color,
        border: `1px solid ${scheme.border}`,
        borderRadius: "999px",
        padding: "4px 12px",
        fontSize: "0.72rem",
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
    };
}

/**
 * Formats a date as DD/MM/YYYY HH:mm or "—" if missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders a small label/value pair used in the header date row.
 * @param {Object} props
 * @param {string} props.label - Label text
 * @param {string} props.value - Value text
 * @returns {React.ReactElement}
 */
function MiniInfo({ label, value }) {
    return (
        <Box>
            <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.65rem" textTransform="uppercase" letterSpacing="0.5px">
                {label}
            </Text>
            <Text color="rgba(255, 255, 255, 0.9)" fontSize="0.8rem" fontWeight={600}>
                {value}
            </Text>
        </Box>
    );
}

/**
 * Renders the tier and status chips next to the close button.
 * @param {Object} props
 * @param {Object} props.sub - Subscription object
 * @param {Function} props.onClose - Drawer close callback
 * @returns {React.ReactElement}
 */
function HeaderChipsRow({ sub, onClose }) {
    const tierScheme = TIER_COLORS[sub?.subscription_type] || DEFAULT_CHIP_COLOR;
    const statusScheme = STATUS_COLORS[sub?.status] || DEFAULT_CHIP_COLOR;
    const tierLabel = TIER_LABELS[sub?.subscription_type] || sub?.subscription_type || "—";
    const statusLabel = STATUS_LABELS[sub?.status] || sub?.status || "—";
    return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Box {...chipBoxProps(tierScheme)}>{tierLabel}</Box>
                <Box {...chipBoxProps(statusScheme)}>{statusLabel}</Box>
            </Stack>
            <IconButton aria-label="Fermer" onClick={onClose} size="sm" variant="ghost"
                color="rgba(255, 255, 255, 0.7)" icon={<Icon as={MdClose} boxSize="18px" />} />
        </Stack>
    );
}

/**
 * Drawer header: tier+status chips, ID, period, amount, auto-renew flag.
 * @param {Object} props
 * @param {Object} props.sub - Subscription object
 * @param {Function} props.onClose - Drawer close callback
 * @returns {React.ReactElement}
 */
function SubscriptionDetailHeader({ sub, onClose }) {
    return (
        <Box pb={4} borderBottom="1px solid rgba(255, 255, 255, 0.08)">
            <HeaderChipsRow sub={sub} onClose={onClose} />
            <Text color="rgba(255, 255, 255, 0.45)" fontSize="0.7rem" fontFamily="monospace" mb={3}>
                ID&nbsp;: {sub?.id || "—"}
            </Text>
            <Stack direction="row" spacing={6} flexWrap="wrap" rowGap={2}>
                <MiniInfo label="Du" value={formatDateTime(sub?.start_date)} />
                <MiniInfo label="Au" value={formatDateTime(sub?.end_date)} />
                <MiniInfo label="Montant" value={sub?.amount != null ? `${sub.amount} ${sub.currency || ""}`.trim() : "—"} />
                <MiniInfo label="Paiement" value={PROVIDER_LABELS[sub?.payment_provider] || sub?.payment_provider || "—"} />
                <MiniInfo label="Auto-renouv." value={sub?.auto_renew ? "Oui" : "Non"} />
            </Stack>
        </Box>
    );
}

export default SubscriptionDetailHeader;
